import React, { useCallback, useMemo, useState } from "react";
import {
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { Field, FieldProps, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import AppListHeader from "@/features/ui/app-list-header";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppSeasonSelect from "@/features/line-sheet-sets/app-season-select";
import AppSelect from "@/features/line-sheet-sets/app-select";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import AppTextArea from "@/features/ui/app-text-area";
import AppPriceInput, { PriceInputValue } from "@/features/invoices/app-price-input";
import InvoiceFileFormControl from "@/features/invoices/order-confirmation-invoices/invoice-file-form-control";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useGetIssuedThroughOrIssuedTo from "@/features/invoices/hooks/use-get-issued-through-or-issued-to";
import useGetDenseCreditInvoice, {
  GetDenseCreditInvoiceParameter,
  GetDenseCreditInvoiceRequest,
} from "@/features/invoices/credit-invoices/hooks/use-get-dense-credit-invoice";
import { CreditInvoiceFormFields } from "@/features/invoices/credit-invoices/upload-credit-invoice-page";
import useCreditInvoiceDumbTransaction from "@/features/invoices/credit-invoices/hooks/use-credit-invoice-dumb-transaction";
import useUpdateCreditInvoice, {
  UpdateCreditInvoicePathParameter,
  UpdateCreditInvoiceRequest,
} from "@/features/invoices/credit-invoices/hooks/use-update-credit-invoice";
import { ProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import { CompanyType, isFile } from "@/features/types";
import PriceUtils from "@/features/ui/utils/price-utils";
import { toTitle } from "@/utils/case";

interface Props {
  creditInvoiceId: number;
  isOpen: boolean;
  onClose: () => void;
  /** 저장이 끝난 뒤(목록 갱신용). */
  onSuccess?: () => void;
  /** 이 오더의 프로포르마들 — 원인 프로포르마 선택지와 이름 표시에 쓴다. */
  proformaInvoices?: Pick<ProformaInvoice, "id" | "name" | "status">[];
}

/**
 * 크레딧 보기/수정 모달 — 오더 상세의 크레딧/디파짓 탭에서 행을 눌렀을 때.
 * 크레딧 발행 모달(add-credit-invoice-modal)과 같은 2단 레이아웃. 보기 모드에서는 전부 읽기 전용이고,
 * 수정 모드에서 아직 프로포르마에 쓰이지 않은 크레딧은 금액·연결까지 전부, 이미 쓰인 크레딧은
 * 소유자·수수료 차감 여부·코멘트만 고칠 수 있다(백엔드도 같은 기준으로 막는다).
 */
export default function EditCreditInvoiceModal({
  creditInvoiceId,
  isOpen,
  onClose,
  onSuccess,
  proformaInvoices = [],
}: Props) {
  const identity = useIdentity();
  const { t, tTitle } = useI18nHelper();
  const { error: showError } = useAppToasts();
  const [isEditing, setIsEditing] = useState(false);
  const { withMessage } = useYupHelper({
    ns: "credit_invoices",
    prefix: "upload_credit_invoices_form",
  });

  const key = useMemo(():
    | {
        parameter: GetDenseCreditInvoiceParameter;
        request: GetDenseCreditInvoiceRequest;
      }
    | undefined => {
    if (identity?.company && creditInvoiceId) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
          creditInvoiceId,
        },
        request: {},
      };
    }
  }, [creditInvoiceId, identity]);

  const { data: creditInvoice, mutate: mutateGetCreditInvoice } =
    useGetDenseCreditInvoice(key?.parameter, key?.request);

  const issuedThroughOrIssuedTo = useGetIssuedThroughOrIssuedTo();

  const initialValues = useMemo((): CreditInvoiceFormFields => {
    if (creditInvoice) {
      return {
        id: creditInvoice.id,
        number: creditInvoice.number,
        type: creditInvoice.type,
        file: creditInvoice.file || undefined,
        amount: creditInvoice.amount,
        issuedOn: creditInvoice.issuedOn,
        name: creditInvoice.name,
        season: creditInvoice.season,
        orderedBy: creditInvoice.issuedToCompany,
        issuedBy: creditInvoice.issuedByCompany,
        issuedThrough:
          creditInvoice.issuedThroughCompany ||
          issuedThroughOrIssuedTo.issuedThrough,
        sendAsNewEmail: creditInvoice.emailThreadMessageId === null,
        emailThreadMessageId: creditInvoice.emailThreadMessageId || undefined,
        privateComment: creditInvoice.privateComment || undefined,
        publicComment: creditInvoice.publicComment || undefined,
        earningProformaInvoices: creditInvoice.earningProformaInvoices,
        orderConfirmationInvoices: creditInvoice.orderConfirmationInvoices,
        usingProformaInvoices: creditInvoice.usingProformaInvoices,
        ownedByCompanyType: creditInvoice.ownedByCompany?.type || undefined,
        isUnoFeeDeductible: creditInvoice.isUnoFeeDeductible ?? false,
        isUsed: creditInvoice.isUsed,
      };
    }
    return {
      earningProformaInvoices: [],
      orderConfirmationInvoices: [],
      usingProformaInvoices: [],
      sendAsNewEmail: false,
      ...issuedThroughOrIssuedTo,
    };
  }, [creditInvoice, issuedThroughOrIssuedTo]);

  // 원인 프로포르마 선택지는 살아 있는 것만, 이름 표시는 전부에서 찾는다(이미 연결된 취소 건도 이름은 보여야 한다).
  const earningCandidates = useMemo(
    () =>
      proformaInvoices.filter(
        (p) => p.status !== "CANCELED" && p.status !== "REPLACED"
      ),
    [proformaInvoices]
  );
  const proformaNameById = useMemo(
    () => new Map(proformaInvoices.map((p) => [p.id, p.name])),
    [proformaInvoices]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        season: Yup.string().required(withMessage.required),
        issuedOn: Yup.date().required(withMessage.required),
        amount: Yup.object({
          value: Yup.number()
            .positive(withMessage.positive)
            .required(withMessage.required),
          currency: Yup.string().required(withMessage.required),
        }).required(withMessage.required),
        publicComment: Yup.string().trim().required(withMessage.required),
        earningProformaInvoices: Yup.array().test(
          "fixing-needs-earning-proforma",
          t(
            "credit_invoices:upload_credit_invoices_form.messages.error.either_order_confirm_invoice_or_proforma_invoice_if_type_is_fixing"
          ),
          function (value) {
            if (this.parent.type !== "FIXING") {
              return true;
            }
            return (value?.length ?? 0) > 0;
          }
        ),
      }),
    [withMessage, t]
  );

  const validate = useCallback(
    async (values: CreditInvoiceFormFields) => {
      try {
        await validationSchema.validate(values, { abortEarly: false });
      } catch (e) {
        showError(e);
        return e;
      }
    },
    [validationSchema, showError]
  );

  const { addUpdate } = useCreditInvoiceDumbTransaction();
  const { fire: fireUpdateCreditInvoice } = useUpdateCreditInvoice();

  const handleSubmit = useCallback(
    async (values: CreditInvoiceFormFields) => {
      if (!identity?.company) {
        return false;
      }
      const parameter: UpdateCreditInvoicePathParameter = {
        by: identity.company.type,
        companyId: identity.company.id,
        creditInvoiceId: values.id!,
      };
      const request: Omit<UpdateCreditInvoiceRequest, "file"> = {
        id: values.id!,
        name: values.name!,
        number: values.number!,
        season: values.season!,
        type: values.type!,
        issuedOn: values.issuedOn!,
        issuedByCompany: values.issuedBy!,
        issuedToCompany: values.orderedBy!,
        issuedThroughCompany: values.issuedThrough!,
        amount: PriceUtils.placeholder(values.amount!, "EUR"),
        emailThreadMessageId: values.emailThreadMessageId || null,
        publicComment: values.publicComment || null,
        privateComment: values.privateComment || null,
        ownedByCompanyType: values.ownedByCompanyType || null,
        isUnoFeeDeductible:
          values.type === "FIXING" && !!values.isUnoFeeDeductible,
        orderConfirmationInvoices: values.orderConfirmationInvoices.map(
          (ocInvoice) => ({ id: ocInvoice.id })
        ),
        earningProformaInvoices: values.earningProformaInvoices.map(
          (pInvoice) => ({ id: pInvoice.id })
        ),
      };

      if (isFile(values.file!)) {
        // 새 파일이 있으면 업로드 큐(dumb transaction)를 탄다.
        await addUpdate(parameter, { ...request, file: values.file! });
      } else {
        await fireUpdateCreditInvoice(parameter, {
          ...request,
          file: values.file!,
        });
      }
      await mutateGetCreditInvoice();
      onSuccess?.();
      return true;
    },
    [identity, addUpdate, fireUpdateCreditInvoice, mutateGetCreditInvoice, onSuccess]
  );

  // 이미 프로포르마에 쓰인 크레딧은 금액·연결·날짜·파일을 못 바꾼다.
  const isLocked = !!creditInvoice?.isUsed;
  const canEditCore = isEditing && !isLocked;
  const canEditMeta = isEditing;

  const createHeaderActions = useCallback(
    (props: FormikProps<CreditInvoiceFormFields>) => {
      if (isEditing) {
        return [
          <AppButton
            key={"cancel"}
            isDisabled={props.isSubmitting}
            onClick={() => {
              props.resetForm();
              setIsEditing(false);
            }}
          >
            {tTitle("cancel")}
          </AppButton>,
          <AppButton
            key={"save"}
            variant={"primary"}
            isLoading={props.isSubmitting}
            isDisabled={props.isSubmitting || !props.dirty}
            onClick={() => props.submitForm()}
          >
            {tTitle("common:save")}
          </AppButton>,
        ];
      }
      return [
        <AppButton key={"close"} onClick={onClose}>
          {tTitle("close")}
        </AppButton>,
        <AppButton
          key={"edit"}
          variant={"primary"}
          onClick={() => setIsEditing(true)}
        >
          {tTitle("common:edit")}
        </AppButton>,
      ];
    },
    [isEditing, onClose, tTitle]
  );

  const labelProps = {
    fontSize: "12px",
    color: "#6F6F6C",
    padding: "0",
    margin: "0",
  } as const;

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius={"8px"} overflow={"hidden"} maxWidth={"800px"}>
        <ModalBody padding={"0"}>
          <Formik<CreditInvoiceFormFields>
            enableReinitialize={true}
            initialValues={initialValues}
            validate={validate}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            onSubmit={async (values, formikHelpers) => {
              formikHelpers.setSubmitting(true);
              try {
                if (await handleSubmit(values)) {
                  setIsEditing(false);
                }
              } catch (e) {
                showError(e);
              } finally {
                formikHelpers.setSubmitting(false);
              }
            }}
          >
            {(formik) => (
              <Grid
                as={"form"}
                height={"100%"}
                width={"100%"}
                gridTemplateColumns={"1fr"}
                gridTemplateRows={"auto 1fr"}
              >
                <AppListHeader
                  headers={[
                    <Heading key={"header"} as={"h2"} fontSize={"14px"}>
                      {creditInvoice?.name ?? tTitle("credit_invoice")}
                    </Heading>,
                  ]}
                  filters={[]}
                  actions={createHeaderActions(formik)}
                  showNavigateToBack={false}
                />
                <Grid
                  padding={"24px"}
                  gap={"12px"}
                  gridTemplateColumns={"1fr 1fr"}
                  overflow={"auto"}
                >
                  <Flex flexDirection={"column"} gap={"8px"}>
                    {isLocked && isEditing && (
                      <Text fontSize={"12px"} color={"#CC1D1D"}>
                        이미 프로포르마에 사용된 크레딧이라 소유자, 수수료 차감 여부, 코멘트만 수정할 수 있습니다.
                      </Text>
                    )}

                    {/* 발행사 — 부티크 또는 에이전시. 만든 뒤에는 바꾸지 않는다(바꾸려면 새로 발행). */}
                    <Field name={"issuedBy"}>
                      {(props: FieldProps<CreditInvoiceFormFields["issuedBy"]>) => (
                        <FormControl display={"flex"} flexDirection={"column"} gap={"4px"}>
                          <FormLabel {...labelProps}>{toTitle(t("issued_by"))}</FormLabel>
                          <AppSelect<number>
                            name={toTitle(t("issued_by"))}
                            width={"100%"}
                            isReadOnly={true}
                            value={props.field.value?.id}
                            options={
                              props.field.value
                                ? [
                                    {
                                      name: `${props.field.value.name} (${toTitle(
                                        t(props.field.value.type === "AGENCY" ? "agency" : "boutique")
                                      )})`,
                                      value: props.field.value.id,
                                    },
                                  ]
                                : []
                            }
                          />
                        </FormControl>
                      )}
                    </Field>

                    <Field name={"season"}>
                      {(props: FieldProps<string>) => (
                        <FormControl display={"flex"} flexDirection={"column"} gap={"4px"} isRequired={canEditCore}>
                          <FormLabel {...labelProps}>{toTitle(t("season"))}</FormLabel>
                          <AppSeasonSelect
                            width={"100%"}
                            value={props.field.value}
                            readonly={!canEditCore}
                            isClearable={false}
                            onSelect={(value) => props.form.setFieldValue(props.field.name, value)}
                          />
                        </FormControl>
                      )}
                    </Field>

                    {/* 하자 크레딧의 원인 프로포르마. 선택지는 이 오더의 프로포르마. */}
                    {formik.values.type === "FIXING" && (
                      <Field name={"earningProformaInvoices"}>
                        {(props: FieldProps<CreditInvoiceFormFields["earningProformaInvoices"]>) => (
                          <FormControl display={"flex"} flexDirection={"column"} gap={"4px"} isRequired={canEditCore}>
                            <FormLabel {...labelProps}>{toTitle(t("earning_proforma_invoices"))}</FormLabel>
                            <AppSelect<number>
                              name={toTitle(t("earning_proforma_invoices"))}
                              width={"100%"}
                              isMultiple={true}
                              isSearchable={true}
                              isReadOnly={!canEditCore}
                              value={props.field.value.map((p) => p.id)}
                              options={[
                                ...earningCandidates.map((p) => ({ name: p.name, value: p.id })),
                                // 이미 연결돼 있지만 선택지에 없는 프로포르마(취소·대체·다른 오더)도 이름이 보이게.
                                ...props.field.value
                                  .filter((p) => !earningCandidates.some((c) => c.id === p.id))
                                  .map((p) => ({
                                    name: proformaNameById.get(p.id) ?? p.name ?? `#${p.id}`,
                                    value: p.id,
                                  })),
                              ]}
                              onSelect={(value) =>
                                props.form.setFieldValue(
                                  props.field.name,
                                  (value ?? []).map((id) => ({ id }))
                                )
                              }
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}

                    {formik.values.type === "FIXING" && (
                      <Field name={"isUnoFeeDeductible"}>
                        {(props: FieldProps<boolean>) => (
                          <FormControl display={"flex"} flexDirection={"column"} gap={"4px"}>
                            <AppCheckBox
                              isChecked={!!props.field.value}
                              isDisabled={!canEditMeta}
                              name={toTitle(t("uno_fee_deductible"))}
                              onChange={(value) => props.form.setFieldValue(props.field.name, value)}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}

                    <Field name={"ownedByCompanyType"}>
                      {(props: FieldProps<CompanyType | undefined>) => (
                        <FormControl display={"flex"} flexDirection={"column"} gap={"4px"}>
                          <FormLabel {...labelProps}>{toTitle(t("owned_by_company_type"))}</FormLabel>
                          <AppSelect
                            name={toTitle(t("owned_by_company_type"))}
                            width={"100%"}
                            value={props.field.value}
                            isReadOnly={!canEditMeta}
                            options={[
                              { name: toTitle(t("agency")), value: "AGENCY" as CompanyType },
                              { name: toTitle(t("buyer")), value: "BUYER" as CompanyType },
                            ]}
                            onSelect={(value) => {
                              props.form.setFieldValue(props.field.name, value);
                              // 바이어 소유로 돌아가면 수입사 선택란이 사라지므로 값도 크레딧의 바이어로 되돌린다.
                              if (value !== "AGENCY" && creditInvoice) {
                                props.form.setFieldValue("orderedBy", creditInvoice.issuedToCompany);
                              }
                            }}
                            isClearable={true}
                          />
                        </FormControl>
                      )}
                    </Field>

                    {/* 에이전시 소유 크레딧만 다른 바이어 앞으로 둘 수 있다. */}
                    {formik.values.ownedByCompanyType === "AGENCY" && (
                      <Field name={"orderedBy"}>
                        {(props: FieldProps<CreditInvoiceFormFields["orderedBy"]>) => (
                          <FormControl display={"flex"} flexDirection={"column"} gap={"4px"} isRequired={canEditCore}>
                            <FormLabel {...labelProps}>{toTitle(t("ordered_by"))}</FormLabel>
                            <AppCompanySelect
                              name={tTitle("ordered_by")}
                              types={["BUYER"]}
                              width={"100%"}
                              id={props.field.value?.id}
                              isReadOnly={!canEditCore}
                              onSelect={(value) =>
                                props.form.setFieldValue(
                                  props.field.name,
                                  value ? { id: value.id, name: value.name } : creditInvoice?.issuedToCompany
                                )
                              }
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}

                    <Field name={"issuedOn"}>
                      {(props: FieldProps<Date>) => (
                        <FormControl display={"flex"} flexDirection={"column"} gap={"4px"} isRequired={canEditCore}>
                          <FormLabel {...labelProps}>{toTitle(t("issued_on"))}</FormLabel>
                          <AppDatePicker
                            width={"100%"}
                            showTimeInput={false}
                            name={tTitle("issued_on")}
                            value={props.field.value}
                            isReadOnly={!canEditCore}
                            onChange={(value) => props.form.setFieldValue("issuedOn", value)}
                          />
                        </FormControl>
                      )}
                    </Field>

                    <Field name={"publicComment"}>
                      {(props: FieldProps<string>) => (
                        <FormControl display={"flex"} flexDirection={"column"} gap={"4px"} isRequired={canEditMeta}>
                          <FormLabel {...labelProps}>{toTitle(t("credit_issue_reason"))}</FormLabel>
                          <AppTextArea
                            fontSize={"12px"}
                            rows={3}
                            isReadOnly={!canEditMeta}
                            value={props.field.value || ""}
                            onChange={(e) => props.form.setFieldValue(props.field.name, e.target.value)}
                          />
                        </FormControl>
                      )}
                    </Field>

                    <Field name={"privateComment"}>
                      {(props: FieldProps<string>) => (
                        <FormControl display={"flex"} flexDirection={"column"} gap={"4px"}>
                          <FormLabel {...labelProps}>{toTitle(t("private_comment"))}</FormLabel>
                          <AppTextArea
                            fontSize={"12px"}
                            rows={3}
                            isReadOnly={!canEditMeta}
                            value={props.field.value || ""}
                            onChange={(e) => props.form.setFieldValue(props.field.name, e.target.value)}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Flex>

                  <Flex flexDirection={"column"} gap={"8px"}>
                    <InvoiceFileFormControl
                      formik={formik}
                      isEditing={canEditCore}
                      isDisabled={!canEditCore}
                    />

                    <Field name={"amount"}>
                      {(props: FieldProps<PriceInputValue>) => (
                        <FormControl display={"flex"} flexDirection={"column"} gap={"4px"} isRequired={canEditCore}>
                          <FormLabel {...labelProps}>{tTitle("amount")}</FormLabel>
                          <AppPriceInput
                            value={props.field.value}
                            isReadOnly={!canEditCore}
                            onChange={(value) => props.form.setFieldValue(props.field.name, value)}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Flex>
                </Grid>
              </Grid>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
